<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CardsKanban from './components/CardsKanban.vue';
import CardsTable from './components/CardsTable.vue';
import CardsFilterPopover from './components/CardsFilterPopover.vue';
import CardShowModal from './components/CardShowModal.vue';
import CardNewDialog from './components/CardNewDialog.vue';
import {
  countActiveFilters,
  filtersToParams,
  filtersToRoute,
  parseFilters,
} from './cardFilters';
import { useCardsBoard } from './useCardsBoard';
import { useCardsList } from './useCardsList';
import { useCardTaskTypes } from './useCardTaskTypes';

const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const currentUser = useMapGetter('getCurrentUser');
const { taskTypes, fetchTaskTypes } = useCardTaskTypes();
const {
  pipelines,
  pipeline,
  stages,
  columns,
  isLoading,
  totalCount,
  loadPipelines,
  selectPipeline,
  fetchBoard,
  loadMore,
  moveCard,
} = useCardsBoard();
const list = useCardsList();

// The board (one column per stage) or the list (one table across stages), remembered per browser.
const VIEWS = computed(() => [
  {
    key: 'kanban',
    icon: 'i-lucide-square-kanban',
    label: t('CARDS.INDEX.VIEW.KANBAN'),
  },
  {
    key: 'table',
    icon: 'i-lucide-table-2',
    label: t('CARDS.INDEX.VIEW.TABLE'),
  },
]);
const VIEW_KEY = 'zimobi-cards-view';
const view = ref(
  localStorage.getItem(VIEW_KEY) === 'table' ? 'table' : 'kanban'
);
watch(view, value => localStorage.setItem(VIEW_KEY, value));
const isTable = computed(() => view.value === 'table');

const showFilters = ref(false);
const newCardDialog = ref(null);

const filters = computed(() => parseFilters(route));
const activeFilterCount = computed(() => countActiveFilters(filters.value));
const params = computed(() =>
  filtersToParams(filters.value, currentUser.value.id)
);

// The stage filter picks the columns; the index colors each badge as on the full pipeline.
const visibleStages = computed(() =>
  stages.value
    .map((stage, index) => ({ ...stage, index }))
    .filter(
      stage =>
        !filters.value.stageIds.length ||
        filters.value.stageIds.includes(stage.id)
    )
);

// Cards live on several boards (Chamados, the lead boards, the agency's own), so the board shown
// is the one picked in the sidebar, else Chamados.
const activePipelines = computed(() =>
  pipelines.value.filter(item => item.status === 'active')
);
const pipelineId = computed(
  () =>
    filters.value.pipelineId ||
    activePipelines.value.find(item => item.system_key === 'cards')?.id ||
    activePipelines.value[0]?.id
);
const statusLabels = computed(() => pipeline.value?.status_labels || {});

// The list asks for the whole board at once; the stage filter, which picks the board's columns,
// narrows it instead.
const listParams = computed(() => ({
  ...params.value,
  pipeline_id: pipeline.value?.id,
  ...(filters.value.stageIds.length && { stage_id: filters.value.stageIds }),
}));

const loadList = async (more = false) => {
  try {
    await (more
      ? list.loadMore(listParams.value)
      : list.fetchList(listParams.value));
  } catch {
    useAlert(t('CARDS.INDEX.LOAD_ERROR'));
  }
};

const refresh = () =>
  isTable.value ? loadList() : fetchBoard(visibleStages.value, params.value);

const shownCount = computed(() =>
  isTable.value ? list.count.value : totalCount.value
);
const isBusy = computed(() =>
  isTable.value ? list.isLoading.value : isLoading.value
);

const applyFilters = next =>
  router.replace(filtersToRoute(next, route.params.accountId));

const onSearch = debounce(
  event => applyFilters({ ...filters.value, search: event.target.value }),
  300
);

const onMove = async (card, target) => {
  try {
    await moveCard(card, target);
  } catch {
    useAlert(t('CARDS.INDEX.MOVE_ERROR'));
    refresh();
  }
};

// The ticket opens in a modal over the board, addressed by `?card=` so the board keeps its
// filters behind it, the link can be shared, and the browser's back button closes it.
const openCardId = computed(() => Number(route.query.card) || null);

const openCard = card =>
  router.push({ ...route, query: { ...route.query, card: card.id } });

// Closing goes back where the card was opened from: the board itself, or the conversation whose
// card opened it, so the history never holds the board twice either. Only a card reached through
// a link, with nothing in the app behind it, falls back to its board.
const hasPreviousPage = () => !!router.options.history.state.back;

const closeCard = () => {
  if (hasPreviousPage()) {
    router.back();
    return;
  }
  const { card, task, ...query } = route.query;
  router.replace({ ...route, query });
};

// On a phone the board has no sidebar: it goes back where the agent came from (usually the
// conversation whose card opened it), or to the conversations when it was opened directly.
const goBack = () => {
  if (hasPreviousPage()) router.back();
  else
    router.push({
      name: 'home',
      params: { accountId: route.params.accountId },
    });
};

const onCardCreated = card => {
  refresh();
  openCard(card);
};

// An edited ticket reloads the board once the modal goes away, however it was closed.
const isBoardStale = ref(false);
watch(openCardId, id => {
  if (id || !isBoardStale.value) return;
  isBoardStale.value = false;
  refresh();
});

// Loads the board's stages when the board changes, then its cards.
const load = async () => {
  if (pipelineId.value !== pipeline.value?.id)
    await selectPipeline(pipelineId.value);
  await refresh();
};

onMounted(async () => {
  await Promise.all([loadPipelines(), fetchTaskTypes()]);
  await load();
});

// One key for everything a fetch depends on. The route name check skips the change of route
// that takes the user away from the board; the pipeline check, the moment before it loads.
watch(
  () =>
    JSON.stringify([
      pipelineId.value,
      params.value,
      filters.value.stageIds,
      view.value,
    ]),
  () => route.name?.startsWith('cards_') && pipeline.value && load()
);
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <header
      class="flex flex-wrap items-center flex-shrink-0 gap-3 px-4 py-3 sm:px-6 min-h-16"
    >
      <Button
        icon="i-lucide-arrow-left"
        slate
        ghost
        sm
        class="sm:hidden rtl:rotate-180"
        :title="$t('CARDS.INDEX.BACK')"
        data-testid="cards-back"
        @click="goBack"
      />
      <Icon
        :icon="pipeline?.icon || 'i-lucide-kanban'"
        class="hidden sm:block size-5 text-n-slate-11"
      />
      <h1 class="mb-0 text-lg font-medium truncate text-n-slate-12">
        {{ pipeline?.name }}
      </h1>
      <span
        class="text-sm text-n-slate-10 whitespace-nowrap"
        data-testid="cards-count"
      >
        {{ $t('CARDS.INDEX.COUNT', { count: shownCount }, shownCount) }}
      </span>
      <Spinner v-if="isBusy" :size="16" class="text-n-slate-10" />

      <div
        class="flex flex-wrap items-center w-full gap-2 sm:flex-nowrap sm:w-auto ltr:ml-auto rtl:mr-auto"
      >
        <Input
          :model-value="filters.search"
          type="search"
          :placeholder="$t('CARDS.INDEX.SEARCH_PLACEHOLDER')"
          class="min-w-0 basis-full sm:basis-auto sm:w-64"
          :custom-input-class="[
            'h-8 [&:not(.focus)]:!border-transparent bg-n-alpha-2 dark:bg-n-solid-1 ltr:!pl-8 !py-1 rtl:!pr-8',
          ]"
          @input="onSearch"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute -translate-y-1/2 text-n-slate-11 size-4 top-1/2 ltr:left-2 rtl:right-2"
            />
          </template>
        </Input>

        <div v-on-clickaway="() => (showFilters = false)" class="relative">
          <Button
            icon="i-lucide-list-filter"
            color="slate"
            size="sm"
            variant="ghost"
            data-testid="cards-filter-button"
            :class="{ 'bg-n-alpha-2': showFilters }"
            @click="showFilters = !showFilters"
          >
            <span>{{ $t('CARDS.FILTERS.BUTTON') }}</span>
            <span
              v-if="activeFilterCount"
              class="grid h-4 px-1 text-[10px] font-semibold text-white rounded-full min-w-4 place-content-center bg-n-brand"
              data-testid="cards-filter-count"
            >
              {{ activeFilterCount }}
            </span>
          </Button>
          <CardsFilterPopover
            v-if="showFilters"
            :filters="filters"
            :task-types="taskTypes"
            :stages="stages"
            :status-labels="statusLabels"
            @update="applyFilters"
          />
        </div>
        <div
          class="flex flex-shrink-0 gap-0.5 p-0.5 rounded-lg bg-n-alpha-2 ltr:max-sm:ml-auto rtl:max-sm:mr-auto"
          role="radiogroup"
        >
          <button
            v-for="option in VIEWS"
            :key="option.key"
            v-tooltip.top="option.label"
            type="button"
            role="radio"
            :aria-checked="view === option.key"
            :aria-label="option.label"
            class="grid transition-colors rounded-md size-7 place-content-center"
            :class="
              view === option.key
                ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                : 'text-n-slate-11 hover:text-n-slate-12'
            "
            :data-testid="`cards-view-${option.key}`"
            @click="view = option.key"
          >
            <Icon :icon="option.icon" class="size-4" />
          </button>
        </div>
        <Button
          icon="i-lucide-plus"
          size="sm"
          data-testid="cards-new-button"
          :label="$t('CARDS.NEW_CARD.BUTTON')"
          @click="newCardDialog.open()"
        />
      </div>
    </header>

    <main class="flex-1 min-h-0">
      <CardsTable
        v-if="isTable"
        :cards="list.cards.value"
        :stages="stages"
        :status-labels="statusLabels"
        :has-more="list.hasMore.value"
        :is-loading="list.isLoading.value"
        @open="openCard"
        @load-more="loadList(true)"
      />
      <CardsKanban
        v-else
        :stages="visibleStages"
        :columns="columns"
        :status-labels="statusLabels"
        @open="openCard"
        @move="onMove"
        @load-more="stage => loadMore(stage, params)"
      />
    </main>
    <CardShowModal
      v-if="openCardId"
      :key="openCardId"
      :card-id="openCardId"
      :task-id="Number(route.query.task) || null"
      @close="closeCard"
      @change="isBoardStale = true"
    />
    <CardNewDialog
      ref="newCardDialog"
      :pipeline-id="pipeline?.id"
      @created="onCardCreated"
    />
  </section>
</template>
